import type { Observation } from "./signals";
import type { Signal } from "./types";

/**
 * Погода → числа для экрана.
 *
 * Слой эффектов раньше получал ОДНУ строку из семи (`ambientOf`), и на этой
 * границе терялось всё, что ядро посчитало: ливень 8 мм и морось 0.1 мм
 * давали одинаковый `data-w="rain"`, а дождь с ветром рисовался как просто
 * дождь, потому что лестница `if` отдавала победителю всё.
 *
 * Здесь вместо категории — вектор. Поля независимы и складываются: осадки
 * могут идти под наклоном от настоящего ветра, поверх дымки, с грозовыми
 * вспышками. Всё в 0..1, кроме угла в градусах, — поверхности остаётся
 * умножать, а не выбирать.
 */
export interface Scene {
  /** Осадки: что сыплется, сколько и как быстро */
  precip: { kind: "rain" | "snow" | "none"; density: number; fall: number };
  /** Ветер: наклон осадков в градусах от вертикали (минус — влево), сила, рывки */
  wind: { angle: number; strength: number; shear: number };
  /** Пелена: туман и дымка сплошной облачности */
  veil: { fog: number; haze: number };
  /** Свет: солнечное свечение и частота грозовых вспышек */
  light: { glow: number; flash: number };
}

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Наклон осадков: 35° на сильном ветру, дальше почти не растёт. */
const MAX_TILT = 55;

export function toScene(signals: Signal[], o: Observation): Scene {
  const at = (key: string) => signals.find((s) => s.key === key);
  const power = (key: string) => at(key)?.intensity ?? 0;

  // Какие осадки — решают сигналы, сколько их — наблюдение.
  //
  // Интенсивность сигнала здесь не годится: её шкалы калибровались под подбор
  // тегов и на границе rain/heavy_rain не монотонны. 2.49 мм дают rain с
  // интенсивностью 1.0, а 2.50 мм — heavy_rain с 0.31, то есть более сильный
  // дождь выглядел бы слабее. Миллиметры в час такого разрыва не имеют.
  const wet = at("rain") ?? at("heavy_rain");
  const snowy = at("snow");
  const mm = Math.max(0, o.precip);

  const kind: Scene["precip"]["kind"] = snowy ? "snow" : wet ? "rain" : "none";
  // Корень, а не прямая: вдвое больше воды — не вдвое больше капель на глаз.
  const density = kind === "snow" ? clamp01(0.2 + Math.sqrt(mm) / 2.2)
    : kind === "rain" ? clamp01(0.12 + Math.sqrt(mm) / 3.2)
    : 0;
  const fall = kind === "snow" ? 0.1 + density * 0.12
    : kind === "rain" ? 0.6 + density * 0.4
    : 0;

  // Ветер. Направление метеорологическое — откуда дует, — поэтому восточный
  // (90°) уносит капли на запад, то есть по экрану влево: отсюда минус.
  const speed = Math.max(o.wind, o.gusts * 0.8);
  const tilt = MAX_TILT * (1 - Math.exp(-speed / 45));
  const angle = o.windDir == null ? 0 : -Math.sin((o.windDir * Math.PI) / 180) * tilt;
  const strength = clamp01(speed / 60);
  const shear = o.wind > 0 ? clamp01((o.gusts - o.wind) / (o.wind * 1.5)) : 0;

  return {
    precip: { kind, density, fall },
    wind: { angle, strength, shear },
    veil: {
      fog: clamp01(power("fog")),
      // Сплошная облачность до сих пор не имела визуального выражения вовсе.
      haze: clamp01(power("overcast") * 0.4),
    },
    light: {
      // Свечение — солнечное. Ночью ясное небо выглядит иначе, чем днём.
      glow: o.isDay ? clamp01(power("clear")) : 0,
      flash: clamp01(power("storm")),
    },
  };
}
